import { useEffect, useRef, useState } from 'react';
import { zoomToFit } from './actions';
import { PaneContext, useBoard, useBoardMeta, useYMap } from './context';
import { SharePanel } from './SharePanel';
import { LOOKS, sanitizeCss, type BoardMetaSettings } from './BoardSettings';
import { ExportPanel } from './export/ExportPanel';
import { ShortcutsPanel } from './ShortcutsPanel';
import { registerPane } from './paneRegistry';
import { Inspector } from './Inspector';
import { createPaneStore } from './paneStore';
import { StatusBar } from './StatusBar';
import { Toolbar } from './Toolbar';
import { setActivePane, useKeyboard } from './useKeyboard';
import { Viewport } from './viewport/Viewport';
import { applyView, ViewBar } from './views/ViewBar';
import { BoardSettings } from './BoardSettings';
import { PanelContext, type PanelKind } from './panels';
import { LayersPanel } from './layers/LayersPanel';
import { filterSelectable } from './ink';

/**
 * One view onto a board: viewport, toolbar, inspector, its own camera and selection.
 * Split view (Phase 6) renders two of these against the same BoardDoc.
 */
export function BoardPane({ initialView = null }: { initialView?: string | null }) {
  const board = useBoard();
  const [pane] = useState(() => createPaneStore());
  const [panel, setPanel] = useState<PanelKind>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => (rootRef.current ? registerPane(rootRef.current, { board, pane }) : undefined), [board, pane]);
  useEffect(() => {
    const open = () => setPanel((p) => (p === 'shortcuts' ? null : 'shortcuts'));
    window.addEventListener('vds:shortcuts', open);
    return () => window.removeEventListener('vds:shortcuts', open);
  }, []);
  const meta = useBoardMeta();
  const readOnly = meta.role === 'viewer';
  useKeyboard(board, pane, readOnly);
  const look = useYMap<unknown>(board.meta) as BoardMetaSettings;
  const lookName = look.look && LOOKS.some((l) => l.id === look.look) ? look.look : undefined;
  const customCss = look.customCss ? sanitizeCss(look.customCss) : '';

  // Dev-only handle for scripted testing from the console (never in production builds).
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const w = window as unknown as { __vos?: unknown };
    w.__vos = { board, pane };
    return () => {
      if (w.__vos && (w.__vos as { pane: unknown }).pane === pane) delete w.__vos;
    };
  }, [board, pane]);

  // Frame the content once the viewport knows its size: the requested view, else fit everything.
  useEffect(() => {
    const ctx = { board, pane };
    const frame = () => {
      if (!(initialView && applyView(board, pane, initialView))) zoomToFit(ctx);
    };
    if (pane.getState().size.w) {
      frame();
      return;
    }
    const unsub = pane.subscribe((s, prev) => {
      if (!prev.size.w && s.size.w) {
        frame();
        unsub();
      }
    });
    return unsub;
  }, [board, pane, initialView]);

  // Ink belongs to the draw tool, everything else to the other tools: crossing over drops the
  // part of the selection the new tool cannot act on.
  useEffect(
    () =>
      pane.subscribe((s, prev) => {
        if (s.tool === prev.tool || (s.tool === 'draw') === (prev.tool === 'draw') || !s.selection.size) return;
        const keep = filterSelectable(board, s.selection, s.tool);
        if (keep.length !== s.selection.size) s.select(keep);
      }),
    [board, pane],
  );

  // Strokes from before ink layers existed get their layer once the board has loaded (and, for
  // server boards, merged with the server), so the select tool ignores them from the start.
  useEffect(() => {
    if (readOnly) return;
    let cancelled = false;
    let unsub: (() => void) | undefined;
    void board.whenReady.then(() => {
      if (cancelled) return;
      const provider = board.provider;
      if (!provider) return board.adoptStrokes();
      const run = () => {
        if (provider.store.getState().status !== 'synced') return;
        unsub?.();
        unsub = undefined;
        board.adoptStrokes();
      };
      unsub = provider.store.subscribe(run);
      run();
    });
    return () => {
      cancelled = true;
      unsub?.();
    };
  }, [board, readOnly]);

  // Drop selection/editing references to items that no longer exist (undo, remote delete).
  useEffect(
    () =>
      board.index.subscribe(() => {
        const s = pane.getState();
        if (s.editingId && !board.index.has(s.editingId)) s.setEditing(null);
        if (!s.selection.size) return;
        const alive = [...s.selection].filter((id) => board.index.has(id));
        if (alive.length !== s.selection.size) s.select(alive);
      }),
    [board, pane],
  );

  return (
    <PaneContext.Provider value={pane}>
      <PanelContext.Provider value={{ panel, setPanel }}>
        <div
          ref={rootRef}
          className="pane"
          data-look={lookName}
          data-board={board.boardId}
          style={look.accent ? ({ '--accent': look.accent } as React.CSSProperties) : undefined}
          onPointerDownCapture={() => setActivePane(pane)}
        >
          {customCss && <style>{`@scope (.pane[data-board="${board.boardId}"]) {\n${customCss}\n}`}</style>}
          <div className="pane-main">
            <Viewport />
            <Toolbar />
            <ViewBar />
            {panel === 'settings' ? (
              <BoardSettings onClose={() => setPanel(null)} />
            ) : panel === 'layers' ? (
              <LayersPanel onClose={() => setPanel(null)} />
            ) : panel === 'share' ? (
              <SharePanel onClose={() => setPanel(null)} />
            ) : panel === 'export' ? (
              <ExportPanel onClose={() => setPanel(null)} />
            ) : panel === 'shortcuts' ? (
              <ShortcutsPanel onClose={() => setPanel(null)} />
            ) : readOnly ? null : (
              <Inspector />
            )}
            <StatusBar />
          </div>
        </div>
      </PanelContext.Provider>
    </PaneContext.Provider>
  );
}
